import type { DepositOSConfig } from "@gizmolab/depositos";
import type { IframeRenderType } from "./config";

/**
 * postMessage protocol between parent window and Lab Pay iframe.
 *
 * Parent → iframe:
 *   { type: "depositos:config",  config: DepositOSConfig }   — set / update config
 *   { type: "depositos:displayType", displayType: "inline" | "popup" | "button", buttonText?: string } — update render style
 *
 * iframe → Parent:
 *   { type: "depositos:ready" }                            — iframe loaded
 *   { type: "depositos:success", txHash?: string }         — deposit succeeded
 *   { type: "depositos:close" }                            — user dismissed widget
 *   { type: "depositos:resize",  height: number }          — content height changed
 *   { type: "depositos:error",   error: string }           — configuration error
 */

export type InboundMessage = {
  type: "depositos:config";
  config: Partial<DepositOSConfig>;
} | {
  type: "depositos:displayType";
  displayType: IframeRenderType;
  buttonText?: string;
};

export type OutboundMessage =
  | { type: "depositos:ready" }
  | { type: "depositos:success"; txHash?: string }
  | { type: "depositos:close" }
  | { type: "depositos:resize"; height: number }
  | { type: "depositos:error"; error: string };

export function postToParent(msg: OutboundMessage) {
  if (window.parent && window.parent !== window) {
    window.parent.postMessage(msg, "*");
  }
}

export function onParentMessage(
  handler: (msg: InboundMessage) => void
): () => void {
  const listener = (event: MessageEvent) => {
    const data = event.data;
    if (data && typeof data === "object" && typeof data.type === "string") {
      if (data.type === "depositos:config" || data.type === "depositos:displayType") {
        handler(data as InboundMessage);
      }
    }
  };

  window.addEventListener("message", listener);
  return () => window.removeEventListener("message", listener);
}
